import React, {useCallback, useEffect, useMemo, useState} from 'react';
import useTranslate from "../../../../modules/local/useTranslate";
import {BsBrushFill, BsCheck2, BsPenFill, BsPersonFill} from "react-icons/bs";
import {PiPencilSimpleLight} from "react-icons/pi";


/**
 * Hiển thị câu hỏi trắc nghiệm/Đúng Sai (đã được chấm tự động)
 */
const AutoCorrectedQuestion = ({question, index}) => {
    const t = useTranslate();
    const isCorrect = question.score === question.max_score && question.is_corrected === 1;

    return (
        <div
            className="mb-4 rounded-r-xl overflow-hidden bg-white border border-l-0"
            style={{ borderLeft: `3px solid ${isCorrect ? '#639922' : '#E24B4A'}` }}
        >
            {/* Header — tiêu đề chiếm full width */}
            <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-800 leading-snug">
                    {`${t("question")} ${index + 1}: ${question.q}`}
                </h3>
            </div>

            {/* Options */}
            <div className="px-4 py-3 space-y-1">
                {question.ops?.map((op, i) => {
                    const isStudentAnswer = op.is_answer === 1;
                    const isCorrectOption = op.is_correct === 1;

                    let optClass = 'bg-gray-50 text-gray-500';
                    let icon = null;

                    if (isStudentAnswer && isCorrectOption) {
                        optClass = 'bg-green-50 text-green-900';
                        icon = (
                            <span className="w-4 h-4 rounded-full bg-green-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">✓</span>
                        );
                    } else if (isStudentAnswer && !isCorrectOption) {
                        optClass = 'bg-red-50 text-red-900';
                        icon = (
                            <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">✗</span>
                        );
                    } else if (!isStudentAnswer && isCorrectOption) {
                        optClass = 'bg-amber-50 text-amber-900';
                        icon = (
                            <span className="w-4 h-4 rounded-full bg-amber-400 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">✓</span>
                        );
                    }

                    return (
                        <div key={i} className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm ${optClass}`}>
                            {icon ?? <span className="w-4 flex-shrink-0"/>}
                            <span>({t(op.value)}). {t(op.label)}</span>
                        </div>
                    );
                })}
            </div>

            {/* Footer — badge góc phải dưới */}
            <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-end gap-2">
                <span className="text-xs text-gray-400">
                    {question.score} / {question.max_score} {t("pts")}
                </span>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                    isCorrect
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                }`}>
                    {isCorrect ? t("true") : t("false")}
                </span>
            </div>
        </div>
    );
};

/**
 * Hiển thị và chấm điểm câu hỏi Tự luận (Essay)
 */
const EssayQuestionGrader = ({question, index, onScoreUpdate}) => {
    const t = useTranslate();
    const [tempScore, setTempScore] = useState(
        question?.score % 1 === 0
            ? question?.score?.toString()
            : question?.score?.toFixed(1)
    );
    const maxScore = question?.max_score;

    const handleScoreChange = (e) => setTempScore(e.target.value);

    const handleBlur = () => {
        let num = parseFloat(tempScore);
        if (isNaN(num) || num < 0) num = 0;
        else if (num > maxScore) num = maxScore;
        const formattedScore = num % 1 === 0 ? num.toString() : num.toFixed(1);
        setTempScore(formattedScore);
        onScoreUpdate(question?.id, num);
    };

    const currentScore = parseFloat(tempScore) || 0;
    const isScored = question?.is_corrected === 1 || currentScore > 0;

    return (
        <div
            className="mb-4 rounded-r-xl overflow-hidden bg-white border border-l-0"
            style={{ borderLeft: `3px solid ${isScored ? '#378ADD' : '#E24B4A'}` }}
        >
            {/* Header — tiêu đề full width */}
            <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-800 leading-snug">
                    {`${t("question")} ${index + 1}: ${question?.q}`}
                    <span className="ml-1 font-normal text-gray-400">({t("essay")})</span>
                </h3>
            </div>

            {/* Student answer */}
            <div className="px-4 py-3">
                <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                    <p className="text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                        {t("student answer")}
                    </p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {question?.a || (
                            <span className="italic text-gray-400">{t("no answer")}</span>
                        )}
                    </p>
                </div>
            </div>

            {/* Footer — score input + badge góc phải dưới */}
            <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between gap-3">
                <label
                    htmlFor={`score-${question?.id}`}
                    className="flex items-center gap-2 text-xs text-gray-400"
                >
                    <PiPencilSimpleLight className="w-3.5 h-3.5 flex-shrink-0"/>
                    {t("max")} {maxScore} {t("pts")}
                </label>

                <div className="flex items-center gap-2">
                    <input
                        id={`score-${question?.id}`}
                        type="number"
                        value={tempScore}
                        onChange={handleScoreChange}
                        onBlur={handleBlur}
                        step="0.1"
                        min="0"
                        max={maxScore}
                        className="w-16 h-8 px-1 border border-gray-300 rounded-lg text-sm font-bold text-center text-gray-800  focus:border-blue-400 focus:ring-1 focus:ring-blue-200 transition-all"
                    />
                    <span className="text-xs text-gray-400">/ {maxScore}</span>
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                        isScored
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-red-100 text-red-800'
                    }`}>
                        {isScored ? t("graded") : t("not graded")}
                    </span>
                </div>
            </div>
        </div>
    );
};


const CourseClassExamAttemptEvaluateForm = ({attempt, course_class_exam, onSubmit}) => {
    const t = useTranslate();
    // Gán prop attempt vào state, sử dụng tên khác để tránh shadowing
    const [attemptState, setAttemptState] = useState(attempt || {});
    const [isPassed, setIsPassed] = useState(null);

    // Tính toán lại Tổng điểm và Trạng thái Đậu/Rớt
    const {totalScore, passed} = useMemo(() => {
        const calculatedScore = attemptState.questions.reduce((sum, q) => sum + (q.score || 0), 0);
        // Giả định: Đậu nếu đạt 50% tổng điểm tối đa của bài thi
        const passThreshold = course_class_exam.passed_score;
        const passed = calculatedScore >= passThreshold ? 1 : 0;

        return {
            totalScore: calculatedScore,
            passed
        };
    }, [JSON.stringify(attemptState), course_class_exam]);

    useEffect(() => {
        setIsPassed(passed)
    }, [passed])

    // Cập nhật điểm của một câu hỏi (dùng cho câu hỏi Tự luận)
    const handleScoreUpdate = useCallback((questionId, newScore) => {
        setAttemptState(prevAttempt => {
            const updatedQuestions = prevAttempt.questions.map(q => {
                if (q.id === questionId) {
                    return {...q, score: newScore, is_corrected: 1};
                }
                return q;
            });

            return {
                ...prevAttempt,
                questions: updatedQuestions,
            };
        });
    }, []);

    // Hàm lưu kết quả chấm điểm (thực tế sẽ gọi API)
    const handleSaveGrade = () => {
        // Dữ liệu cuối cùng để gửi đi
        const finalData = {
            ...attemptState,
            score: totalScore,
            score_percent: (totalScore / attemptState.max_score) * 100,
            is_passed: isPassed,
            questions: attemptState.questions.map(q => ({
                id: q.id,
                score: q.score,
                is_corrected: q.is_corrected || 0, // Đảm bảo trạng thái đã chấm
            })),
        };

        onSubmit({
            questions_json: JSON.stringify(finalData?.questions),
            is_passed: finalData?.is_passed,
            status: 9
        })

        // // Thay thế alert bằng UI thông báo trong ứng dụng thực tế
        // console.log("Dữ liệu lưu cuối cùng (Gửi API):", finalData);
        // window.alert(`Đã mô phỏng lưu kết quả.\nĐiểm tổng: ${totalScore.toFixed(1)}/${attemptState.max_score}.\nTrạng thái: ${isPassed ? 'Đậu' : 'Rớt'}`);
    };

    const passFailColor = isPassed ? 'text-green-600 bg-green-100 border-green-400' : 'text-red-600 bg-red-100 border-red-400';
    // Lớp CSS động cho nút ĐẬU (Passed) - Được chọn: Green, Chưa chọn: Gray
    const passedClasses = passed
        ? "bg-green-600 text-white shadow-lg shadow-green-400/50" // Đã chọn: Xanh lá đậm, chữ trắng, đổ bóng nổi
        : "text-gray-500 hover:text-green-700 hover:bg-gray-100 transition duration-300"; // Chưa chọn: Màu xám, hover đổi màu chữ sang xanh, nền sáng nhẹ

    // Lớp CSS động cho nút RỚT (Failed) - Được chọn: Red, Chưa chọn: Gray
    const failedClasses = !passed
        ? "bg-red-600 text-white shadow-lg shadow-red-400/50" // Đã chọn: Đỏ đậm, chữ trắng, đổ bóng nổi
        : "text-gray-500 hover:text-red-700 hover:bg-gray-100 transition duration-300"; // Chưa chọn: Màu xám, hover đổi màu chữ sang đỏ, nền sáng nhẹ

    // Lớp CSS cho container chung
    // Đã loại bỏ border-4 để giao diện đơn giản và thanh lịch hơn
    const containerClasses = `
        max-w-xs w-full bg-white p-1 rounded-xl shadow-2xl transition-all duration-500 
    `;

    return (
        <div className="font-sans mb-32">
            <div className="max-w-3xl mx-auto">

                {/* Tiêu đề & Thông tin chung */}
                <header className="mb-6 p-4 bg-white rounded-xl shadow-items-xs border-l-4 border-blue-600 bg-blue-50">
                    <h1 className="mb-3 font-bold text-xl tracking-wide">
                        <BsPenFill size={16} className="text-blue-500 mr-2"/>
                        {course_class_exam.title}
                    </h1>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        {/* Box Thông tin Học sinh */}
                        <div className="p-3 background-50 rounded-lg border border-color-50">
                            <div className="flex items-center text-indigo-700 font-bold mb-1">
                                <BsPersonFill size={16} className="mr-2"/> {t('students')}
                            </div>
                            <p className="font-semibold text-color-000 tracking-wide">{attemptState.student.fullname} ({attemptState.student.student_idcode})</p>
                            <p className="text-xs text-color-300 italic">{t('holy name')}: {attemptState.student.patron_saint_name}</p>
                        </div>

                        {/* Box Thông tin Bài thi */}
                        <div className="p-3 background-50 rounded-lg border border-color-50">
                            <div className="flex items-center text-yellow-500 font-bold mb-1">
                                <BsBrushFill size={16} className="mr-2"/> {t('exam')}
                            </div>
                            <p className="font-semibold text-color-000 tracking-wide">{course_class_exam?.title}</p>
                            <p className="text-xs text-color-300 italic">{t('time')}: {course_class_exam?.duration_minutes} {t('minutes')}</p>
                        </div>
                    </div>
                </header>

                {/* Danh sách Câu hỏi */}
                <section className="space-y-6">
                    <h2 className="text-xl font-bold text-gray-700 border-b pb-2 mb-4">
                        {t('exam details')} ({attemptState?.questions?.length} {t('sentence')})
                    </h2>
                    {attemptState?.questions?.map((q, index) => {
                        if (q.type === 'essay') {
                            return (
                                <EssayQuestionGrader
                                    key={q.id}
                                    question={q}
                                    index={index}
                                    onScoreUpdate={handleScoreUpdate}
                                />
                            );
                        } else {
                            return (
                                <AutoCorrectedQuestion
                                    key={q.id}
                                    question={q}
                                    index={index}
                                />
                            );
                        }
                    })}
                </section>

            </div>

            {/* Footer cố định: Hiển thị Tổng điểm và nút Lưu */}
            <footer className="fixed bottom-0 left-0 right-0 bg-white shadow-2xl p-4 border-t border-gray-200 z-10">
                <div className="max-w-3xl mx-auto flex justify-between items-center flex-wrap">

                    {/* Kết quả Tạm thời */}
                    <div className="flex flex-col text-left mb-2 sm:mb-0">
                        <p className="text-sm text-gray-500 font-medium">{t("current total score")}</p>
                        <div className="flex items-baseline">
                            <span
                                className="text-3xl font-extrabold text-blue-600 mr-1">{totalScore % 1 === 0 ? totalScore : totalScore.toFixed(2)}</span>
                            <span className="text-base text-gray-600"> / {attemptState.max_score}</span>
                        </div>
                    </div>


                    <div id="segment-container" className={containerClasses}>
                        <div className="flex space-x-1" role="tablist">
                            <div
                                data-value="1"
                                className={`
                            flex-1 py-3 text-center cursor-pointer rounded-xl font-bold text-lg select-none 
                            ${passedClasses}
                        `}
                            >
                                {t("passed")}
                            </div>
                            <div
                                data-value="0"
                                className={`
                            flex-1 py-3 text-center cursor-pointer rounded-xl font-bold text-lg select-none 
                            ${failedClasses}
                        `}
                            >
                                {t("failed")}
                            </div>
                        </div>
                    </div>

                    {/* Nút Lưu */}
                    <button
                        onClick={handleSaveGrade}
                        className="w-full flex items-center uppercase w-full justify-center px-6 py-3 bg-green-600 text-white font-bold rounded-xl shadow-lg hover:bg-green-700 transition-colors transform">
                        <BsCheck2 size={18} className="inline-block mr-2"/>
                        {t("save grading result")}
                    </button>

                </div>
            </footer>
        </div>
    );
};

export default CourseClassExamAttemptEvaluateForm;
